import React, { useEffect } from "react";
import { withStyles } from "src/decorators";
import { useRadioGroupContext } from "./RadioGroup.context";
import styled, { css } from "styled-components";
import { Text } from "src/components";
import { Tick } from "@asphalt-react/iconpack";

type Props = React.HTMLProps<HTMLInputElement> & {
  innerRef?: React.Ref<HTMLInputElement>;
  value: any;
  label?: React.ReactNode;
  checkbox?: boolean;
  radius?: number;
};

const defaultRadius = 16;

const RadioWrapperEl = styled.label`
  align-items: center;
  cursor: pointer;
  display: flex;
  min-height: 32px;
`;

const RadioCheckEl = styled.div<{
  isChecked: boolean;
  disabled?: boolean;
  checkbox?: boolean;
  radius: number;
}>`
  border: 1px solid ${(p) => p.theme.colors.borders.active};
  box-sizing: border-box;
  position: relative;
  transition: all 200ms ease-in-out;

  ${(p) => css`
    border-radius: ${p.radius / 2}px;
    margin-right: ${p.radius}px;
    max-height: ${p.radius}px;
    max-width: ${p.radius}px;
    min-height: ${p.radius}px;
    min-width: ${p.radius}px;
  `}

  ${(p) =>
    p.checkbox &&
    css`
      padding: 3px;
      svg {
        display: none;
      }
    `}

  :after {
    background-color: ${(p) => p.theme.colors.borders.brand};
    border-radius: 6px;
    bottom: 0;
    display: block;
    content: "";
    height: 6px;
    left: 0;
    margin: auto;
    position: absolute;
    right: 0;
    transform: scale3d(0, 0, 0);
    transition: all 200ms ease-in-out;
    top: 0;
    width: 6px;
  }

  ${(p) =>
    p.isChecked &&
    css`
      border-color: ${p.theme.colors.borders.brand};
      :after {
        transform: scale3d(1, 1, 1);
      }
    `}

  ${(p) =>
    p.checkbox &&
    p.isChecked &&
    css`
      svg {
        display: inline;
        path {
          fill: #fff;
        }
      }
      background-color: ${p.theme.colors.borders.brand};
      :after {
        display: none;
      }
    `}

  ${(p) =>
    p.disabled &&
    css`
      border-color: ${p.theme.colors.text.disabled};

      :after {
        background-color: ${p.theme.colors.text.disabled};
      }
    `}
`;

const RadioBase = (props: Props) => {
  const context = useRadioGroupContext();
  const {
    innerRef,
    value,
    onChange = context.onChange,
    name = context.name,
    defaultValue = context.currentValue,
    disabled = context.disabled,
    className,
    label,
    style,
    checkbox,
    radius = defaultRadius,
    ...other
  } = props;
  const [isChecked, setIsChecked] = React.useState(defaultValue === value);

  const _name = context.name ?? name;

  const _onChange = () => {
    onChange?.(value);
  };

  useEffect(() => {
    setIsChecked(context.currentValue === value);
  }, [context.currentValue, value]);

  return (
    <RadioWrapperEl className={className} style={style}>
      <input
        {...other}
        type="radio"
        name={_name}
        ref={innerRef}
        disabled={disabled}
        onChange={_onChange}
        style={{ display: "none" }}
      />
      <RadioCheckEl
        isChecked={isChecked}
        disabled={disabled}
        checkbox={checkbox}
        radius={radius}
      >
        {checkbox && <Tick />}
      </RadioCheckEl>
      <Text fontWeight="book">{label}</Text>
    </RadioWrapperEl>
  );
};

export const Radio = withStyles(RadioBase);
